import AppPaths from '../../AppPaths';
import FiMiniImage from '../../assets/images/compare-fi-mini.png';
import FiUltraImage from '../../assets/images/compare-fi-ultra.png';
import FiS3BlueImage from '../../assets/images/compare-s3-blue.png';
import FiS3GrayImage from '../../assets/images/compare-s3-gray.png';
import FiS3PinkImage from '../../assets/images/compare-s3-pink.png';
import FiS3YellowImage from '../../assets/images/compare-s3-yellow.png';
import type { ReasonIconName, SectionIconName } from './CompareIcons';

/**
 * CompareV2 data. Copy is stored as i18n keys (resolved with `t()` at render);
 * feature values mirror the current components/Compare/Compare.tsx.
 */

export const YES = 'yes' as const;
export const NO = 'no' as const;

/** A spec value with an optional sub-line. */
export interface SpecValue {
  valueKey: string;
  subKey?: string;
}

export type AttrValue = typeof YES | typeof NO | SpecValue;

export type ProductKey = 'mini' | 's3' | 'ultra' | 'minicat';

/** A "Top reasons to buy" entry: an icon + one-line benefit. */
export interface ReasonItem {
  icon: ReasonIconName;
  labelKey: string;
}

/** A collar-color option (S3 only): gradient swatch + the matching render. */
export interface Swatch {
  color: string;
  image: string;
}

export interface ProductInfo {
  key: ProductKey;
  /** Card name; also the "Shop X" button label unless shopNameKey overrides. */
  nameKey: string;
  /** Shorter name for the "Shop X" button when the full name would wrap (S3). */
  shopNameKey?: string;
  /** Dropdown option label (only differs from nameKey for the cats variant). */
  dropdownLabelKey: string;
  /** Bold one-liner under the name. */
  taglineKey: string;
  descKey: string;
  image: string;
  route: string;
  /** Stable, untranslated name for analytics. */
  trackName: string;
  reasons: ReasonItem[];
  /** Tap-to-change collar colors (S3 only). */
  swatches?: Swatch[];
  attrs: Record<string, AttrValue>;
}

export const PRODUCTS: Record<ProductKey, ProductInfo> = {
  mini: {
    key: 'mini',
    nameKey: 'compare_v2_name_mini',
    dropdownLabelKey: 'compare_v2_name_mini',
    taglineKey: 'compare_v2_tagline_mini',
    descKey: 'compare_v2_desc_mini',
    image: FiMiniImage,
    route: `${AppPaths.Products('fi-mini')}?type=dog`,
    trackName: 'Mini',
    reasons: [
      { icon: 'dog', labelKey: 'compare_v2_reason_mini_best' },
      { icon: 'link', labelKey: 'compare_v2_reason_works_with_gear' },
      { icon: 'health', labelKey: 'compare_v2_reason_same_tracking' },
    ],
    attrs: {
      live: YES,
      escape: YES,
      walkhistory: { valueKey: 'compare_v2_walkhistory_unlimited' },
      users: YES,
      activity: YES,
      sleep: YES,
      aibehavior: YES,
      fiintel: YES,
      vetreport: YES,
      aidocs: YES,
      applewatch: YES,
      battery: { valueKey: 'compare_v2_battery_2mo', subKey: 'compare_v2_battery_2mo_sub' },
      attach: { valueKey: 'compare_v2_attach_collar_more' },
      weight: { valueKey: 'compare_v2_weight_16' },
      dims: { valueKey: 'compare_v2_dims_mini' },
      charge: { valueKey: 'compare_v2_charge_usbc' },
      waterproof: { valueKey: 'compare_v2_waterproof_ip68' },
      coverage: { valueKey: 'compare_v2_coverage_verizon', subKey: 'compare_v2_coverage_cellular_sub' },
      nightlight: NO,
      satellite: NO,
      recall: NO,
    },
  },
  s3: {
    key: 's3',
    nameKey: 'compare_v2_name_s3',
    shopNameKey: 'compare_v2_shopname_s3',
    dropdownLabelKey: 'compare_v2_name_s3',
    taglineKey: 'compare_v2_tagline_s3',
    descKey: 'compare_v2_desc_s3',
    image: FiS3YellowImage,
    route: AppPaths.Products('smart-collar-v3'),
    trackName: 'Series 3+',
    reasons: [
      { icon: 'dog', labelKey: 'compare_v2_reason_s3_best' },
      { icon: 'shield', labelKey: 'compare_v2_reason_s3_integrated' },
      { icon: 'battery', labelKey: 'compare_v2_reason_s3_battery' },
    ],
    swatches: [
      { color: 'linear-gradient(315deg,#eae101,#fffa01)', image: FiS3YellowImage },
      { color: 'linear-gradient(315deg,#0059d5,#0f74ff)', image: FiS3BlueImage },
      { color: 'linear-gradient(315deg,#555555,#737373)', image: FiS3GrayImage },
      { color: 'linear-gradient(315deg,#c92781,#e94ca3)', image: FiS3PinkImage },
    ],
    attrs: {
      live: YES,
      escape: YES,
      walkhistory: { valueKey: 'compare_v2_walkhistory_unlimited' },
      users: YES,
      activity: YES,
      sleep: YES,
      aibehavior: YES,
      fiintel: YES,
      vetreport: YES,
      aidocs: YES,
      applewatch: YES,
      battery: { valueKey: 'compare_v2_battery_3mo' },
      attach: { valueKey: 'compare_v2_attach_replaces' },
      weight: { valueKey: 'compare_v2_weight_52' },
      dims: { valueKey: 'compare_v2_dims_s3' },
      charge: { valueKey: 'compare_v2_charge_fibase' },
      waterproof: { valueKey: 'compare_v2_waterproof_ip68_66k' },
      coverage: { valueKey: 'compare_v2_coverage_att', subKey: 'compare_v2_coverage_cellular_sub' },
      nightlight: YES,
      satellite: NO,
      recall: NO,
    },
  },
  ultra: {
    key: 'ultra',
    nameKey: 'compare_v2_name_ultra',
    dropdownLabelKey: 'compare_v2_name_ultra',
    taglineKey: 'compare_v2_tagline_ultra',
    descKey: 'compare_v2_desc_ultra',
    image: FiUltraImage,
    route: AppPaths.Products('fi-ultra'),
    trackName: 'Fi Ultra',
    reasons: [
      { icon: 'dog', labelKey: 'compare_v2_reason_ultra_gps' },
      { icon: 'link', labelKey: 'compare_v2_reason_works_with_gear' },
      { icon: 'callback', labelKey: 'compare_v2_reason_ultra_callback' },
    ],
    attrs: {
      live: YES,
      escape: YES,
      walkhistory: { valueKey: 'compare_v2_walkhistory_unlimited' },
      users: YES,
      activity: NO,
      sleep: NO,
      aibehavior: NO,
      fiintel: NO,
      vetreport: NO,
      aidocs: NO,
      applewatch: YES,
      battery: { valueKey: 'compare_v2_battery_5day', subKey: 'compare_v2_battery_5day_sub' },
      attach: { valueKey: 'compare_v2_attach_collar_harness' },
      weight: { valueKey: 'compare_v2_weight_68' },
      dims: { valueKey: 'compare_v2_dims_ultra' },
      charge: { valueKey: 'compare_v2_charge_usbc' },
      waterproof: { valueKey: 'compare_v2_waterproof_ip68_66k' },
      coverage: { valueKey: 'compare_v2_coverage_att_tmobile', subKey: 'compare_v2_coverage_ltem_sub' },
      nightlight: NO,
      satellite: YES,
      recall: YES,
    },
  },
  minicat: {
    key: 'minicat',
    nameKey: 'compare_v2_name_minicat',
    dropdownLabelKey: 'compare_v2_label_minicat',
    taglineKey: 'compare_v2_tagline_minicat',
    descKey: 'compare_v2_desc_minicat',
    image: FiMiniImage,
    route: `${AppPaths.Products('fi-mini')}?type=cat`,
    trackName: 'Mini',
    reasons: [
      { icon: 'cat', labelKey: 'compare_v2_reason_cat_best' },
      { icon: 'link', labelKey: 'compare_v2_reason_works_with_gear' },
      { icon: 'health', labelKey: 'compare_v2_reason_same_tracking' },
    ],
    attrs: {
      live: YES,
      escape: YES,
      walkhistory: { valueKey: 'compare_v2_walkhistory_unlimited' },
      users: YES,
      activity: YES,
      sleep: YES,
      aibehavior: NO,
      fiintel: YES,
      vetreport: YES,
      aidocs: YES,
      applewatch: YES,
      battery: { valueKey: 'compare_v2_battery_2mo', subKey: 'compare_v2_battery_2mo_sub' },
      attach: { valueKey: 'compare_v2_attach_collar_more' },
      weight: { valueKey: 'compare_v2_weight_16' },
      dims: { valueKey: 'compare_v2_dims_mini' },
      charge: { valueKey: 'compare_v2_charge_usbc' },
      waterproof: { valueKey: 'compare_v2_waterproof_ip68' },
      coverage: { valueKey: 'compare_v2_coverage_verizon', subKey: 'compare_v2_coverage_cellular_sub' },
      nightlight: NO,
      satellite: NO,
      recall: NO,
    },
  },
};

export const PRODUCT_ORDER: ProductKey[] = ['mini', 's3', 'ultra', 'minicat'];
export const DESKTOP_DEFAULT: ProductKey[] = ['mini', 's3', 'ultra'];
export const MOBILE_DEFAULT: ProductKey[] = ['mini', 's3'];

export type TooltipKey = 'aibehavior' | 'fiintel';

export interface AttrRow {
  labelKey: string;
  /** Key into `ProductInfo.attrs`. */
  attrKey: string;
  /** When set, an info icon opens the matching modal. */
  tooltip?: TooltipKey;
}

export interface Section {
  titleKey: string;
  icon: SectionIconName;
  rows: AttrRow[];
}

/** Attributes grouped by category (tracking & health first, off-grid last). */
export const SECTIONS: Section[] = [
  {
    titleKey: 'compare_v2_section_tracking',
    icon: 'tracking',
    rows: [
      { labelKey: 'compare_v2_attr_live', attrKey: 'live' },
      { labelKey: 'compare_v2_attr_escape', attrKey: 'escape' },
      { labelKey: 'compare_v2_attr_walkhistory', attrKey: 'walkhistory' },
      { labelKey: 'compare_v2_attr_users', attrKey: 'users' },
    ],
  },
  {
    titleKey: 'compare_v2_section_health',
    icon: 'health',
    rows: [
      { labelKey: 'compare_v2_attr_activity', attrKey: 'activity' },
      { labelKey: 'compare_v2_attr_sleep', attrKey: 'sleep' },
      { labelKey: 'compare_v2_attr_aibehavior', attrKey: 'aibehavior', tooltip: 'aibehavior' },
      { labelKey: 'compare_v2_attr_fiintel', attrKey: 'fiintel', tooltip: 'fiintel' },
      { labelKey: 'compare_v2_attr_vetreport', attrKey: 'vetreport' },
      { labelKey: 'compare_v2_attr_aidocs', attrKey: 'aidocs' },
      { labelKey: 'compare_v2_attr_applewatch', attrKey: 'applewatch' },
    ],
  },
  {
    titleKey: 'compare_v2_section_device',
    icon: 'device',
    rows: [
      { labelKey: 'compare_v2_attr_battery', attrKey: 'battery' },
      { labelKey: 'compare_v2_attr_attach', attrKey: 'attach' },
      { labelKey: 'compare_v2_attr_weight', attrKey: 'weight' },
      { labelKey: 'compare_v2_attr_dims', attrKey: 'dims' },
      { labelKey: 'compare_v2_attr_charge', attrKey: 'charge' },
      { labelKey: 'compare_v2_attr_waterproof', attrKey: 'waterproof' },
      { labelKey: 'compare_v2_attr_coverage', attrKey: 'coverage' },
      { labelKey: 'compare_v2_attr_nightlight', attrKey: 'nightlight' },
    ],
  },
  {
    titleKey: 'compare_v2_section_adventure',
    icon: 'adventure',
    rows: [
      { labelKey: 'compare_v2_attr_satellite', attrKey: 'satellite' },
      { labelKey: 'compare_v2_attr_recall', attrKey: 'recall' },
    ],
  },
];

export interface TooltipItem {
  titleKey: string;
  descKey: string;
}

export interface TooltipContent {
  titleKey: string;
  introKey: string;
  items: TooltipItem[];
  outroKey?: string;
}

export const TOOLTIPS: Record<TooltipKey, TooltipContent> = {
  aibehavior: {
    titleKey: 'compare_v2_tt_aibehavior_title',
    introKey: 'compare_v2_tt_aibehavior_intro',
    items: [
      { titleKey: 'compare_v2_tt_aibehavior_1_t', descKey: 'compare_v2_tt_aibehavior_1_d' },
      { titleKey: 'compare_v2_tt_aibehavior_2_t', descKey: 'compare_v2_tt_aibehavior_2_d' },
      { titleKey: 'compare_v2_tt_aibehavior_3_t', descKey: 'compare_v2_tt_aibehavior_3_d' },
      { titleKey: 'compare_v2_tt_aibehavior_4_t', descKey: 'compare_v2_tt_aibehavior_4_d' },
      { titleKey: 'compare_v2_tt_aibehavior_5_t', descKey: 'compare_v2_tt_aibehavior_5_d' },
    ],
    outroKey: 'compare_v2_tt_aibehavior_outro',
  },
  fiintel: {
    titleKey: 'compare_v2_tt_fiintel_title',
    introKey: 'compare_v2_tt_fiintel_intro',
    items: [
      { titleKey: 'compare_v2_tt_fiintel_1_t', descKey: 'compare_v2_tt_fiintel_1_d' },
      { titleKey: 'compare_v2_tt_fiintel_2_t', descKey: 'compare_v2_tt_fiintel_2_d' },
    ],
    outroKey: 'compare_v2_tt_fiintel_outro',
  },
};
